import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { Footer } from '@/components/layouts/Footer';
import { ContactCards } from '@/components/sections/ContactCards';
import { useContactForm } from '@/hooks/useContactForm';
import { Input } from '@/components/ui/Input';
import { useContactPageData } from './useContactPageData';

/**
 * R2 - Contact page with multiple communication channels
 * R3 - Data resolution delegated to useContactPageData to stay under complexity limit
 */
export function ContactoPage() {
  const d = useContactPageData();
  const { formData, errors, isSubmitting, handleChange, handleSubmit } = useContactForm(d.whatsappNumber);

  return (
    <>
      <section className="py-20 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-inboca-teal hover:text-inboca-teal/80 font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al Inicio
          </Link>

          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {d.pageTitle}
              </h1>
              <p className="text-lg text-gray-700">
                {d.pageDesc}
              </p>
            </div>

            <ContactCards
              email={d.email}
              phone={d.phone}
              phoneRaw={d.phoneRaw}
              address={d.address}
              whatsappUrl={d.whatsappUrl}
            />
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="max-w-2xl mx-auto mt-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-inboca-teal to-inboca-teal/90 px-8 sm:px-12 py-12">
              <h2 className="text-3xl font-bold text-white mb-2">
                {d.formTitle}
              </h2>
              <p className="text-white/90">
                {d.formDesc}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 sm:p-12 space-y-6">
              {/* Name Input */}
              <Input
                label="Nombre Completo"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                {...(errors.name ? { error: errors.name } : {})}
                placeholder="Tu nombre"
                required
              />

              {/* Phone Input */}
              <Input
                label="Teléfono"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                {...(errors.phone ? { error: errors.phone } : {})}
                placeholder="Tu número de teléfono"
                required
              />

              {/* Message Textarea */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  Mensaje *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Cuéntanos sobre tu proyecto"
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-inboca-teal focus:border-transparent transition-all text-base bg-white resize-none"
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-inboca-orange hover:bg-inboca-orange/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              >
                <FaWhatsapp className="h-5 w-5" />
                {isSubmitting ? 'Enviando...' : 'Enviar por WhatsApp'}
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer data={d.footer} contactInfo={d.contactInfo} />
    </>
  );
}
